import type { ReactNode } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { Info24px, SWReactIcons } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { Checkbox } from "@similarweb/ui-components";
import { Flex, Stack, Text } from "@similarweb/ui-components/foundations";

import { StyledTooltipTrigger, StyledWhiteTooltip } from "./styles";

type WidgetRowProps = {
    title: string;
    description?: string;
    tooltipContent?: ReactNode;
    isChecked: boolean;
    isFixed?: boolean;
    onToggle?: () => void;
    onMouseEnter?: () => void;
    dragHandle?: ReactNode;
    dataAutomation?: string;
};

export const WidgetRow = ({
    title,
    description,
    tooltipContent,
    isChecked,
    isFixed = false,
    onToggle,
    onMouseEnter,
    dragHandle,
    dataAutomation,
}: WidgetRowProps) => {
    const { translate } = useTranslate();

    return (
        <Flex
            onMouseEnter={onMouseEnter}
            alignItems="center"
            gap="12px"
            padding="10px 12px"
            borderRadius="8px"
            border={`1px solid ${colorsPalettes.carbon["50"]}`}
            background={colorsPalettes.carbon["0"]}
            data-automation={dataAutomation}
        >
            {isFixed ? (
                <Flex
                    alignItems="center"
                    justifyContent="center"
                    flexShrink={0}
                    margin="0 8px 0 4px"
                >
                    <SWReactIcons
                        iconName="locked"
                        size="xs"
                        iconColor={colorsPalettes.carbon["200"]}
                    />
                </Flex>
            ) : (
                dragHandle
            )}

            <Flex
                flexDirection="column"
                gap="2px"
                flex="1"
                minWidth="0"
            >
                <Text
                    as="span"
                    fontVariant="body1"
                    color={colorsPalettes.carbon["500"]}
                >
                    {title}
                </Text>
                {description ? (
                    <Stack>
                        <Text
                            as="span"
                            fontVariant="body2"
                            color={colorsPalettes.carbon["250"]}
                        >
                            {description}
                        </Text>
                        {tooltipContent ? (
                            <StyledWhiteTooltip
                                content={tooltipContent}
                                placement="bottom-end"
                            >
                                <StyledTooltipTrigger
                                    alignItems="center"
                                    gap="4px"
                                    width="fit-content"
                                >
                                    <Info24px
                                        size="xs"
                                        color={colorsPalettes.carbon["200"]}
                                        role="tooltip"
                                        aria-label={typeof tooltipContent === "string" ? tooltipContent : undefined}
                                    />
                                    <Text
                                        fontVariant="listItem3"
                                        color={colorsPalettes.carbon["200"]}
                                        data-automation={dataAutomation ? `${dataAutomation}-see-example` : undefined}
                                    >
                                        {translate("si.management.extension.settings.overview_widgets.see_example")}
                                    </Text>
                                </StyledTooltipTrigger>
                            </StyledWhiteTooltip>
                        ) : null}
                    </Stack>
                ) : null}
            </Flex>

            <Flex
                flexShrink={0}
                data-automation={dataAutomation ? `${dataAutomation}-checkbox` : undefined}
            >
                <Checkbox
                    selected={isChecked}
                    isDisabled={isFixed}
                    onClick={isFixed ? undefined : onToggle}
                    size="sm"
                />
            </Flex>
        </Flex>
    );
};
